/**
 * Zod validation schemas for Household entities
 *
 * Validates household data, children profiles, and consent tracking.
 * Matches the authoritative Firestore schema in docs/firestore-schema.md
 */

import { z } from 'zod';
import { LanguageSchema } from './common-schemas';

/**
 * Child profile validation
 * Age can be calculated from birthMonth + birthYear when needed
 */
export const ChildProfileSchema = z.object({
  birthMonth: z.number().min(1).max(12),
  birthYear: z.number().min(1900).max(new Date().getFullYear()),
});

/**
 * Individual consent record validation
 * Records when and what version user agreed to
 */
export const ConsentRecordSchema = z.object({
  accepted: z.boolean(),
  acceptedAt: z.date(),
  version: z.string().optional(),
  explanation: z.string().optional(),
});

/**
 * Terms of service consent (version required)
 */
export const TermsOfServiceConsentSchema = ConsentRecordSchema.extend({
  version: z.string().min(1),
});

/**
 * Photo processing consent (explanation required)
 */
export const PhotoProcessingConsentSchema = ConsentRecordSchema.extend({
  explanation: z.string().min(1),
});

/**
 * Consents validation
 * Structured consent records with versioning for GDPR compliance
 */
export const ConsentsSchema = z.object({
  termsOfService: TermsOfServiceConsentSchema,
  photoProcessing: PhotoProcessingConsentSchema.optional(),
});

/**
 * Usage quotas validation
 * Rate limiting counters for preventing abuse
 */
export const UsageQuotasSchema = z.object({
  stories: z.object({
    count: z.number().min(0),
    resetAt: z.date(),
  }),
  photos: z.object({
    count: z.number().min(0),
    resetAt: z.date(),
  }),
  reads: z.object({
    count: z.number().min(0),
    resetAt: z.date(),
  }),
});

/**
 * Household preferences validation
 */
export const HouseholdPreferencesSchema = z.object({
  language: LanguageSchema,
  analyticsOptOut: z.boolean(),
  emailNotifications: z.boolean(),
});

/**
 * Beta cohort enum
 */
export const BetaCohortSchema = z.enum(['phase1', 'phase2']);

/**
 * Feedback cadence bookkeeping (Epic 25, Story 25.2).
 * All fields optional for back-compat with pre-Epic-25 households.
 */
export const HouseholdFeedbackStateSchema = z.object({
  lastStoryPromptAt: z.date().optional(),
  storyPromptCount: z.number().int().min(0).optional(),
  /** @deprecated Story 25.4 — read-only for migration; superseded by pulseStage. */
  pulseShown: z.boolean().optional(),
  pulseStage: z.number().int().min(0).optional(),
  lastPulseAt: z.date().optional(),
  characterLightShown: z.boolean().optional(),
  characterDetailedShown: z.boolean().optional(),
});

/**
 * Household validation
 * Main household entity with all data
 * Matches docs/firestore-schema.md lines 45-88
 */
export const HouseholdSchema = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  createdAt: z.date(),
  updatedAt: z.date(),
  children: z.array(ChildProfileSchema),
  // TD-VALID-2: Made optional for beta to support legacy accounts missing these fields
  consents: ConsentsSchema.optional(),
  usageQuotas: UsageQuotasSchema.optional(),
  preferences: HouseholdPreferencesSchema.optional(),
  firstStoryCreated: z.boolean().optional(),
  onboardingCompletedAt: z.date().optional(),
  betaCohort: BetaCohortSchema.optional(),
  invitationCode: z.string().optional(),
  /** True if user must change temp password before using app (Story 7A.4) */
  requiresPasswordChange: z.boolean().optional(),
  /** When password was last changed (for pre-created accounts) (Story 7A.4) */
  passwordChangedAt: z.date().optional(),
  /** Feedback cadence bookkeeping (Epic 25, Story 25.2) */
  feedbackState: HouseholdFeedbackStateSchema.optional(),
});

/**
 * @deprecated Use ConsentsSchema instead. Kept for backward compatibility.
 */
export const ConsentTimestampsSchema = z.object({
  photoProcessing: z.date().optional(),
  termsOfService: z.date(),
  privacyPolicy: z.date(),
  lastUpdated: z.date().optional(),
});
